import { EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { FileSpreadsheet } from 'lucide-react';

import AlertIcon from '@/assets/icons/AlertIcon';
import AppStoreIcon from '@/assets/icons/AppleAppStoreIcon';
import FreshdeskIcon from '@/assets/icons/FreshdeskIcon';
import G2Icon from '@/assets/icons/G2Icon';
import GongIcon from '@/assets/icons/GongIcon';
import GooglePlayStoreIcon from '@/assets/icons/GooglePlayStoreIcon';
import HelpScoutIcon from '@/assets/icons/HelpScoutIcon';
import KustomerIcon from '@/assets/icons/KustomerIcon';
import NotAssignedIcon from '@/assets/icons/NotAssignedIcon';
import OkendoIcon from '@/assets/icons/OkendoIcon';
import PriorityHighIcon from '@/assets/icons/PriorityHighIcon';
import PriorityLowIcon from '@/assets/icons/PriorityLowIcon';
import PriorityMidIcon from '@/assets/icons/PriorityMidIcon';
import SentimentNegativeIcon from '@/assets/icons/SentimentNegativeIcon';
import SentimentNeutralIcon from '@/assets/icons/SentimentNeutralIcon';
import SentimentPositiveIcon from '@/assets/icons/SentimentPositiveIcon';
import StampedIcon from '@/assets/icons/StampedIcon';
import TicketCloseOutlined from '@/assets/icons/TicketCloseOutlined';
import TicketOpenOutlined from '@/assets/icons/TicketOpenOutlined';
import TicketPendingOutlined from '@/assets/icons/TicketPendingOutlined';
import TrustpilotIcon from '@/assets/icons/TrustpilotIcon';
import TwitterIcon from '@/assets/icons/TwitterIcon';
import TypeformIcon from '@/assets/icons/TypeformIcon';
import YotpoIcon from '@/assets/icons/YotpoIcon';
import type { SelectOptionT } from '@/components/Select/types';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import { SCRAPER_DOMAIN_NAME } from '@/entities/(integration)/integrations/model/constants';
import lightModeColor from '@/styles/colors/color';
import { ConstMap } from '@/utils/ConstMap';

export enum SOURCE_TYPE {
  MANUAL = 1,
  CSV = 2,
  SLACK = 3,
  CHANNELTALK = 4,
  INTERCOM = 5,
  ZENDESK = 6,
  DISCORD = 7,
  // GITHUB = 8,
  TYPEFORM = 9,
  // HUBSPOT = 10,
  SLACK_SALESFORCE = 11,
  SALESFORCE = 12,
  FRONT = 13,
  // METABASE = 14,
  GORGIAS = 15,
  ATLAS = 16,
  GOOGLE_PLAY = 17,
  APPLE_APP_STORE = 18,
  G2 = 19,
  TWITTER = 20,
  KUSTOMER = 21,
  GONG = 22,
  FRESHDESK = 24,
  STAMPED = 25,
  YOTPO = 26,
  OKENDO = 27,
  TRUSTPILOT = 28,
  KNOCOMMERCE = 29,
  // TIKTOK_SHOP = 30,
  HELPSCOUT = 31,

  // 32~50 스크래핑
  OLIVEYOUNG = 32,
  AMAZON = 33,
  QOO10 = 35, // scraper 없음
  COUPANG = 36,
  NAVER_STORE = 37,
  NAVER_MAP = 38, // scrapper 없음
  GANGNAMUNNI = 39, // scraper 없음
  SEPHORA = 40,
  TIKTOK = 41, // by comment keyword, by profile, by post keyword
  NAVER_CAFE = 42, // all search, in search
  NAVER_BLOG = 43,
  SCRAPED_REDDIT = 44,
  FACEBOOK = 45, // by keyword, by url
  INSTAGRAM = 46, // by keyword, by profile
  DC_INSIDE = 47,
  PPOMPPU = 48,
  GOOGLE_NEWS = 49,
  YOUTUBE = 50,
  GOOGLE_MAP_REVIEW = 51,
  RICH_PANEL = 52,
  THRIVE_MARKET = 53,
  LAZADA = 54,
  SHOPEE = 55,
  INSTAGRAM_REELS = 56,
  FMKOREA = 62,
  // 여기까지

  // 57~60 reserved

  // Survey
  QUALTRICS = 61,
}

export const SCRAPING_SOURCE_TYPE_LIST = [
  SOURCE_TYPE.OLIVEYOUNG,
  SOURCE_TYPE.AMAZON,
  SOURCE_TYPE.QOO10,
  SOURCE_TYPE.COUPANG,
  SOURCE_TYPE.NAVER_STORE,
  SOURCE_TYPE.NAVER_MAP,
  SOURCE_TYPE.GANGNAMUNNI,
  SOURCE_TYPE.SEPHORA,
  SOURCE_TYPE.TIKTOK,
  SOURCE_TYPE.NAVER_CAFE,
  SOURCE_TYPE.NAVER_BLOG,
  SOURCE_TYPE.SCRAPED_REDDIT,
  SOURCE_TYPE.FACEBOOK,
  SOURCE_TYPE.INSTAGRAM,
  SOURCE_TYPE.DC_INSIDE,
  SOURCE_TYPE.PPOMPPU,
  SOURCE_TYPE.GOOGLE_NEWS,
  SOURCE_TYPE.YOUTUBE,
  SOURCE_TYPE.GOOGLE_MAP_REVIEW,
  SOURCE_TYPE.THRIVE_MARKET,
  SOURCE_TYPE.LAZADA,
  SOURCE_TYPE.SHOPEE,
  SOURCE_TYPE.INSTAGRAM_REELS,
  SOURCE_TYPE.FMKOREA,
  SOURCE_TYPE.TWITTER,
];

export const isScrapingSourceType = (source: SOURCE_TYPE): boolean => {
  return SCRAPING_SOURCE_TYPE_LIST.includes(source);
};

export const FeedbackSourceMap: Map<SOURCE_TYPE, string> = new Map([
  [SOURCE_TYPE.MANUAL, 'Manual'],
  [SOURCE_TYPE.CSV, 'CSV / Excel'],
  [SOURCE_TYPE.SLACK, 'Slack'],
  [SOURCE_TYPE.CHANNELTALK, 'ChannelTalk'],
  [SOURCE_TYPE.INTERCOM, 'Intercom'],
  [SOURCE_TYPE.ZENDESK, 'Zendesk'],
  [SOURCE_TYPE.DISCORD, 'Discord'],
  // 8: undefined,
  [SOURCE_TYPE.TYPEFORM, 'Typeform'],
  // 10: undefined,
  [SOURCE_TYPE.SLACK_SALESFORCE, 'Slack <-> Salesforce'],
  [SOURCE_TYPE.SALESFORCE, 'Salesforce'],
  [SOURCE_TYPE.FRONT, 'Front'],
  // 14: undefined,
  [SOURCE_TYPE.GORGIAS, 'Gorgias'],
  [SOURCE_TYPE.ATLAS, 'Atlas'],
  [SOURCE_TYPE.GOOGLE_PLAY, 'Google Play'],
  [SOURCE_TYPE.APPLE_APP_STORE, 'App Store'],
  [SOURCE_TYPE.G2, 'G2'],
  [SOURCE_TYPE.TWITTER, 'X'],
  [SOURCE_TYPE.KUSTOMER, 'Kustomer'],
  [SOURCE_TYPE.GONG, 'Gong'],
  [SOURCE_TYPE.FRESHDESK, 'Freshdesk'],
  [SOURCE_TYPE.STAMPED, 'Stamped'],
  [SOURCE_TYPE.YOTPO, 'Yotpo'],
  [SOURCE_TYPE.OKENDO, 'Okendo'],
  [SOURCE_TYPE.TRUSTPILOT, 'Trustpilot'],
  [SOURCE_TYPE.KNOCOMMERCE, 'Knocommerce'],
  // 30: 'TikTok Shop',
  [SOURCE_TYPE.HELPSCOUT, 'Helpscout'],
  [SOURCE_TYPE.OLIVEYOUNG, 'Oliveyoung'],
  [SOURCE_TYPE.AMAZON, 'Amazon'],
  [SOURCE_TYPE.QOO10, 'Qoo10'],
  [SOURCE_TYPE.COUPANG, 'Coupang'],
  [SOURCE_TYPE.NAVER_STORE, 'Naver Store'],
  [SOURCE_TYPE.NAVER_MAP, 'Naver Map'],
  [SOURCE_TYPE.GANGNAMUNNI, 'Gangnamunni'],
  [SOURCE_TYPE.SEPHORA, 'Sephora'],
  [SOURCE_TYPE.TIKTOK, 'TikTok'],
  [SOURCE_TYPE.NAVER_CAFE, 'Naver Cafe'],
  [SOURCE_TYPE.NAVER_BLOG, 'Naver Blog'],
  [SOURCE_TYPE.SCRAPED_REDDIT, 'Reddit'],
  [SOURCE_TYPE.FACEBOOK, 'Facebook'],
  [SOURCE_TYPE.INSTAGRAM, 'Instagram Posts'],
  [SOURCE_TYPE.DC_INSIDE, 'DC Inside'],
  [SOURCE_TYPE.PPOMPPU, 'Ppomppu'],
  [SOURCE_TYPE.GOOGLE_NEWS, 'Google News'],
  [SOURCE_TYPE.YOUTUBE, 'Youtube'],
  [SOURCE_TYPE.GOOGLE_MAP_REVIEW, 'Google Map Review'],
  [SOURCE_TYPE.RICH_PANEL, 'Richpanel'],
  [SOURCE_TYPE.THRIVE_MARKET, 'Thrive Market'],
  [SOURCE_TYPE.LAZADA, 'Lazada'],
  [SOURCE_TYPE.SHOPEE, 'Shopee'],
  [SOURCE_TYPE.INSTAGRAM_REELS, 'Instagram Reels'],
  [SOURCE_TYPE.FMKOREA, 'FM Korea'],
  [SOURCE_TYPE.QUALTRICS, 'Qualtrics'],
]);

export const getFeedbackSourceName = (source: SOURCE_TYPE): string => {
  return FeedbackSourceMap.get(source) || 'Unknown';
};

export const getFeedbackSourceNameAndDomain = (
  source: SOURCE_TYPE,
  domain: string,
) => {
  if (source === SOURCE_TYPE.YOUTUBE && domain === 'short') {
    return 'Youtube Shorts';
  }

  return getFeedbackSourceName(source);
};

export enum FeedbackSentiment {
  POSITIVE = 1,
  NEUTRAL = 2,
  NEGATIVE = 3,
}

const SentimentIconSize = 14;

export const sentimentMap: Record<FeedbackSentiment, IconValueT> = {
  [FeedbackSentiment.POSITIVE]: {
    value: 'Positive',
    icon: (
      <SentimentPositiveIcon
        width={SentimentIconSize}
        height={SentimentIconSize}
        fill={lightModeColor.sentiment[FeedbackSentiment.POSITIVE]}
      />
    ),
  },
  [FeedbackSentiment.NEUTRAL]: {
    value: 'Neutral',
    icon: (
      <SentimentNeutralIcon
        width={SentimentIconSize}
        height={SentimentIconSize}
        fill={lightModeColor.sentiment[FeedbackSentiment.NEUTRAL]}
      />
    ),
  },
  [FeedbackSentiment.NEGATIVE]: {
    value: 'Negative',
    icon: (
      <SentimentNegativeIcon
        width={SentimentIconSize}
        height={SentimentIconSize}
        fill={lightModeColor.sentiment[FeedbackSentiment.NEGATIVE]}
      />
    ),
  },
};

export const sentimentIconMap = new ConstMap([
  [FeedbackSentiment.POSITIVE, sentimentMap[FeedbackSentiment.POSITIVE].icon],
  [FeedbackSentiment.NEUTRAL, sentimentMap[FeedbackSentiment.NEUTRAL].icon],
  [FeedbackSentiment.NEGATIVE, sentimentMap[FeedbackSentiment.NEGATIVE].icon],
]);

export const sentimentsOptions: SelectOptionT[] = [
  {
    value: 1,
    searchName: 'Positive',
  },
  {
    value: 2,
    searchName: 'Neutral',
  },
  {
    value: 3,
    searchName: 'Negative',
  },
];

export const priorityMap: { [key: number]: IconValueT } = {
  1: {
    value: NOT_ASSIGNED_LABEL,
    icon: <NotAssignedIcon />,
  },
  2: {
    value: 'Low',
    icon: <PriorityLowIcon />,
  },
  3: {
    value: 'Mid',
    icon: <PriorityMidIcon />,
  },
  4: {
    value: 'High',
    icon: <PriorityHighIcon />,
  },
  5: {
    value: 'Urgent',
    icon: <AlertIcon />,
  },
};

export const getSourceIconBySourceTypeAndDomain = ({
  sourceType,
  domain,
  size = 16,
}: {
  sourceType?: SOURCE_TYPE;
  domain?: string;
  size?: number;
}) => {
  if (sourceType === undefined) {
    return <FileSpreadsheet width={size} height={size} />;
  }

  if (
    sourceType === SOURCE_TYPE.YOUTUBE &&
    domain === SCRAPER_DOMAIN_NAME.youtubeShorts
  ) {
    return (
      <img
        src={`/assets/youtube-shorts-icon.svg`}
        width={size}
        height={size}
        alt="youtube-short"
      />
    );
  }

  return getSourceIcon(sourceType, size);
};

export const getSourceIcon = (source?: SOURCE_TYPE, size = 16) => {
  switch (source) {
    case SOURCE_TYPE.MANUAL:
      return <EditOutlined />;
    case SOURCE_TYPE.CSV:
      return <FileTextOutlined />;
    case SOURCE_TYPE.SLACK:
      return (
        <img
          src="/assets/ic-source-slack.svg"
          width={size}
          height={size}
          alt="slack"
        />
      );
    case SOURCE_TYPE.CHANNELTALK:
      return (
        <img
          src={`/assets/ic-source-channeltalk.svg`}
          width={size}
          height={size}
          alt="channeltalk"
        />
      );
    case SOURCE_TYPE.INTERCOM:
      return (
        <img
          src={`/assets/ic-source-intercom.svg`}
          width={size}
          height={size}
          alt="intercom"
        />
      );
    case SOURCE_TYPE.ZENDESK:
      return (
        <img
          src={`/assets/logo-zendesk.svg`}
          width={size}
          height={size}
          alt="zendesk"
        />
      );
    case SOURCE_TYPE.DISCORD:
      return (
        <img
          src={`/assets/logo-discord.svg`}
          width={size}
          height={size}
          alt="zendesk"
        />
      );
    case SOURCE_TYPE.SALESFORCE:
      return (
        <img
          src={`/assets/logo-salesforce.svg`}
          width={size}
          height={size}
          alt="salesforce"
        />
      );
    case SOURCE_TYPE.FRONT:
      return (
        <img
          src={`/assets/ic-source-front.svg`}
          width={size}
          height={size}
          alt="intercom"
        />
      );
    case SOURCE_TYPE.GORGIAS:
      return (
        <img
          src={`/assets/logo-gorgias.svg`}
          width={size}
          height={size}
          alt="intercom"
        />
      );
    case SOURCE_TYPE.ATLAS:
      return (
        <img
          src={`/assets/logo-atlas.png`}
          width={size}
          height={size}
          alt="intercom"
        />
      );
    case SOURCE_TYPE.GOOGLE_PLAY:
      return <GooglePlayStoreIcon width={size} height={size} />;
    case SOURCE_TYPE.APPLE_APP_STORE:
      return <AppStoreIcon width={size} height={size} />;
    case SOURCE_TYPE.G2:
      return <G2Icon width={size} height={size} />;
    case SOURCE_TYPE.TWITTER:
      return <TwitterIcon width={size} height={size} />;
    case SOURCE_TYPE.KUSTOMER:
      return <KustomerIcon width={size} height={size} />;
    case SOURCE_TYPE.GONG:
      return <GongIcon width={size} height={size} />;
    case SOURCE_TYPE.TYPEFORM:
      return <TypeformIcon width={size} height={size} />;
    case SOURCE_TYPE.FRESHDESK:
      return <FreshdeskIcon width={size} height={size} />;
    case SOURCE_TYPE.STAMPED:
      return <StampedIcon width={size} height={size} />;
    case SOURCE_TYPE.YOTPO:
      return <YotpoIcon width={size} height={size} />;
    case SOURCE_TYPE.OKENDO:
      return (
        <OkendoIcon width={size} height={size} style={{ borderRadius: size }} />
      );
    case SOURCE_TYPE.TRUSTPILOT:
      return <TrustpilotIcon width={size} height={size} />;
    case SOURCE_TYPE.KNOCOMMERCE:
      return (
        <img
          src={`/assets/logo-knocommerce.png`}
          width={size}
          height={size}
          alt="intercom"
        />
      );
    case SOURCE_TYPE.HELPSCOUT:
      return <HelpScoutIcon size={size} />;
    case SOURCE_TYPE.OLIVEYOUNG:
      return (
        <img
          src={`/assets/ic-source-oliveyoung.png`}
          width={size}
          height={size}
          alt="oliveyoung"
        />
      );
    case SOURCE_TYPE.AMAZON:
      return (
        <img
          src={`/assets/ic-source-amazon.svg`}
          width={size}
          height={size}
          alt="amazon"
        />
      );
    case SOURCE_TYPE.QOO10:
      return (
        <img
          src={`/assets/ic-source-qoo10.svg`}
          width={size}
          height={size}
          alt="qoo10"
        />
      );
    case SOURCE_TYPE.COUPANG:
      return (
        <img
          src={`/assets/ic-source-coupang.png`}
          width={size}
          height={size}
          alt="coupang"
        />
      );
    case SOURCE_TYPE.NAVER_STORE:
      return (
        <img
          src={`/assets/ic-source-naver-store.svg`}
          width={size}
          height={size}
          alt="naver-store"
        />
      );
    case SOURCE_TYPE.NAVER_MAP:
      return (
        <img
          src={`/assets/ic-source-naver-map.png`}
          width={size}
          height={size}
          alt="naver-map"
        />
      );
    case SOURCE_TYPE.GANGNAMUNNI:
      return (
        <img
          src={`/assets/ic-source-gangnamunni.png`}
          width={size}
          height={size}
          alt="gangnamunni"
        />
      );
    case SOURCE_TYPE.SEPHORA:
      return (
        <img
          src={`/assets/ic-source-sephora.svg`}
          width={size}
          height={size}
          alt="sephora"
        />
      );
    case SOURCE_TYPE.TIKTOK:
      return (
        <img
          src={`/assets/ic-source-tiktok.svg`}
          width={size}
          height={size}
          alt="tiktok"
        />
      );
    case SOURCE_TYPE.NAVER_BLOG:
      return (
        <img
          src={`/assets/ic-source-naver-blog.png`}
          width={size}
          height={size}
          alt="naver-blog"
        />
      );
    case SOURCE_TYPE.NAVER_CAFE:
      return (
        <img
          src={`/assets/ic-source-naver-cafe.png`}
          width={size}
          height={size}
          alt="naver-cafe"
        />
      );
    case SOURCE_TYPE.SCRAPED_REDDIT:
      return (
        <img
          src={`/assets/ic-source-reddit.svg`}
          width={size}
          height={size}
          alt="reddit"
        />
      );
    case SOURCE_TYPE.SLACK_SALESFORCE:
      return (
        <img
          src={`/assets/ic-source-salesforce.svg`}
          width={size}
          height={size}
          alt="salesforce"
        />
      );
    case SOURCE_TYPE.FACEBOOK:
      return (
        <img
          src={`/assets/logo-facebook.svg`}
          width={size}
          height={size}
          alt="facebook-comment"
        />
      );
    case SOURCE_TYPE.INSTAGRAM:
      return (
        <img
          src={`/assets/instagram-color-logo.svg`}
          width={size}
          height={size}
          alt="instagram-comment"
        />
      );
    case SOURCE_TYPE.DC_INSIDE:
      return (
        <img
          src={`/assets/ic-source-dc-inside.png`}
          width={size}
          height={size}
          alt="dc-inside"
        />
      );
    case SOURCE_TYPE.PPOMPPU:
      return (
        <img
          src={`/assets/ic-source-ppomppu.png`}
          width={size}
          height={size}
          alt="ppomppu"
        />
      );
    case SOURCE_TYPE.GOOGLE_NEWS:
      return (
        <img
          src={`/assets/ic-source-google-news.svg`}
          width={size}
          height={size}
          alt="google-news"
        />
      );
    case SOURCE_TYPE.YOUTUBE:
      return (
        <img
          src={`/assets/ic-source-youtube.svg`}
          width={size}
          height={size}
          alt="youtube"
        />
      );
    case SOURCE_TYPE.GOOGLE_MAP_REVIEW:
      return (
        <img
          src={`/assets/ic-source-google-map-review.svg`}
          width={size}
          height={size}
          alt="google-map-review"
        />
      );
    case SOURCE_TYPE.RICH_PANEL:
      return (
        <img
          src={`/assets/ic-source-richpanel.svg`}
          width={size}
          height={size}
          alt="richpanel"
        />
      );
    case SOURCE_TYPE.THRIVE_MARKET:
      return (
        <img
          src={`/assets/ic-source-thrive-market.svg`}
          width={size}
          height={size}
          alt="thrive-market"
        />
      );
    case SOURCE_TYPE.LAZADA:
      return (
        <img
          src={`/assets/ic-source-lazada.svg`}
          width={size}
          height={size}
          alt="lazada"
        />
      );
    case SOURCE_TYPE.SHOPEE:
      return (
        <img
          src={`/assets/ic-source-shopee.svg`}
          width={size}
          height={size}
          alt="shopee"
        />
      );
    case SOURCE_TYPE.INSTAGRAM_REELS:
      return (
        <img
          src={`/assets/instagram-reels-icon.svg`}
          width={size}
          height={size}
          alt="instagram-reels"
        />
      );
    case SOURCE_TYPE.FMKOREA:
      return (
        <img
          src="/assets/logo-fmkorea.webp"
          width={size}
          height={size}
          alt="fmkorea"
        />
      );
    case SOURCE_TYPE.QUALTRICS:
      return (
        <img
          src="/assets/logo-qualtrics.svg"
          width={size}
          height={size}
          alt="qualtrics"
        />
      );
    default:
      return <FileSpreadsheet size={size} />;
  }
};

export const FEEDBACK_DETAIL_MODAL_SCROLL_SELECTOR = (alias: string) =>
  `feedback_detail_modal_scroll_selector_${alias}`;

export enum FEEDBACK_STATUS {
  FULL = 0,
  SENTIMENT_ONLY = 1,
}

export const FeedbackStatusMap: Record<FEEDBACK_STATUS, string> = {
  [FEEDBACK_STATUS.FULL]: 'Full',
  [FEEDBACK_STATUS.SENTIMENT_ONLY]: 'Sentiment Only',
};

export enum SOURCE_STATUS {
  OPEN = 1,
  PENDING = 2,
  CLOSE = 3,
}

export const SourceStatusMap: Record<SOURCE_STATUS, IconValueT> = {
  [SOURCE_STATUS.OPEN]: {
    icon: <TicketOpenOutlined width={16} height={16} />,
    value: 'Open',
  },
  [SOURCE_STATUS.PENDING]: {
    icon: <TicketPendingOutlined width={16} height={16} />,
    value: 'Pending',
  },
  [SOURCE_STATUS.CLOSE]: {
    icon: <TicketCloseOutlined width={16} height={16} />,
    value: 'Closed',
  },
};

export enum SURVEY_ITEM_TYPE {
  CHECKBOX = 'CHECKBOX',
  RADIO = 'RADIO',
  TEXT = 'TEXT',
}

export enum FEEDBACK_TYPE {
  TICKET = 'ticket',
  REVIEW = 'review',
  CHAT_APP = 'chat_app',
  SOCIAL = 'social',
  SURVEY = 'survey',
}

export const feedbackTypeMap = new Map([
  [FEEDBACK_TYPE.TICKET, 'Ticket'],
  [FEEDBACK_TYPE.REVIEW, 'Review'],
  [FEEDBACK_TYPE.CHAT_APP, 'Chat App'],
  [FEEDBACK_TYPE.SOCIAL, 'Social'],
  [FEEDBACK_TYPE.SURVEY, 'Survey'],
]);

export enum FEEDBACK_BODY_TYPE {
  PLAIN_TEXT = 'PLAIN',
  CHAT = 'CHAT',
  THREAD = 'THREADED_CHAT',
  SURVEY = 'SURVEY',
  DISCUSSION = 'DISCUSSION',
}

export const feedbackBodyTypeMap = new Map([
  [FEEDBACK_BODY_TYPE.PLAIN_TEXT, 'Plain Text'],
  [FEEDBACK_BODY_TYPE.CHAT, 'Chat'],
  [FEEDBACK_BODY_TYPE.THREAD, 'Threaded Chat'],
  [FEEDBACK_BODY_TYPE.SURVEY, 'Survey'],
  [FEEDBACK_BODY_TYPE.DISCUSSION, 'Discussion'],
]);

export enum RACE {
  UNKNOWN = 'Unknown',
  WHITE = 'White',
  BLACK = 'Black',
  EAST_ASIAN = 'East Asian',
  SOUTHEAST_ASIAN = 'Southeast Asian',
  SOUTH_ASIAN = 'South Asian',
  MIDDLE_EASTERN = 'Middle Eastern',
  OTHERS = 'Others',
}

export enum GENDER {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  UNKNOWN = 'UNKNOWN',
}

export const genderList = new Map([
  [GENDER.MALE, 'Male'],
  [GENDER.FEMALE, 'Female'],
  [GENDER.UNKNOWN, 'Unknown'],
]);

export enum AGE_GROUP {
  AGE_0S = '0',
  AGE_10S = '10',
  AGE_20S = '20',
  AGE_30S = '30',
  AGE_40S = '40',
  AGE_50S = '50',
  AGE_60S = '60',
  AGE_70S = '70',
  AGE_80S = '80',
  AGE_90S = '90',
  AGE_100S = '100+',
}

export const ageGroupList = new Map([
  [AGE_GROUP.AGE_0S, '0 ~ 9'],
  [AGE_GROUP.AGE_10S, '10 ~ 19'],
  [AGE_GROUP.AGE_20S, '20 ~ 29'],
  [AGE_GROUP.AGE_30S, '30 ~ 39'],
  [AGE_GROUP.AGE_40S, '40 ~ 49'],
  [AGE_GROUP.AGE_50S, '50 ~ 59'],
  [AGE_GROUP.AGE_60S, '60 ~ 69'],
  [AGE_GROUP.AGE_70S, '70 ~ 79'],
  [AGE_GROUP.AGE_80S, '80 ~ 89'],
  [AGE_GROUP.AGE_90S, '90 ~ 99'],
  [AGE_GROUP.AGE_100S, '100 ~'],
]);

export enum AD_PREDICTION {
  COMMERCIAL = 'COMMERCIAL',
  ORGANIC = 'ORGANIC',
  SUSPECTED = 'SUSPECTED',
}

export const adPredictionList = new Map([
  [AD_PREDICTION.COMMERCIAL, 'Commercial'],
  [AD_PREDICTION.ORGANIC, 'Organic'],
  [AD_PREDICTION.SUSPECTED, 'Suspected'],
]);
